"use client";

import { memo, useEffect, useState } from "react";
import Image, { ImageProps } from "next/image";
import { CDN_URL, STATIC_PREFIX } from "@/lib/cdn";

/**
 * External domains that block Next.js Image Optimizer
 * These will be served with unoptimized=true to avoid 403 errors
 *
 * NOTE: Polymarket S3 was removed to test if Image Optimizer works now.
 * This should improve PageSpeed HTTP/2 score. If 403 errors occur, add back.
 */
const UNOPTIMIZED_DOMAINS: string[] = [
  "d1lvyva3zy5u58.cloudfront.net",
  "kalshi-web-cdn.s3.amazonaws.com",
  "kalshi.com",
  // NOTE: polymarket-upload.s3 returns 403 on direct browser access — it's
  // only reachable via server-side fetch (Next.js Image Optimizer). Do NOT
  // list it here; let the optimizer proxy it the way polymarket.com does.
];

/**
 * Check if a URL is from a domain that blocks image optimization
 */
function shouldSkipOptimization(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return UNOPTIMIZED_DOMAINS.some(domain => urlObj.hostname.includes(domain));
  } catch {
    return false;
  }
}

/**
 * CdnImage - Image component that serves static assets from Cloudflare R2 CDN
 *
 * Automatically converts local paths (e.g., "/logo.png") to CDN URLs.
 * Falls back to Next.js Image for external URLs.
 * Adds unoptimized=true for external domains that block Image Optimizer (like Polymarket S3).
 *
 * @example
 * // Before: <Image src="/brand/logo.png" ... />
 * // After:  <CdnImage src="/brand/logo.png" ... />
 *
 * The image will be served from:
 * https://pub-xxx.r2.dev/static/web/brand/logo.png
 */
interface CdnImageProps extends ImageProps {
  fallbackSrc?: string;
}

function CdnImageInner({ src, fallbackSrc, unoptimized, sizes, onError, ...props }: CdnImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setCurrentSrc(src);
  }, [src]);

  // Auto-set sizes for small fixed-width images (avatars, icons) to prevent
  // Next.js from loading 1345px images for 20px avatars (default sizes="100vw")
  const effectiveSizes = sizes ||
    (typeof props.width === "number" && props.width <= 120 ? `${props.width}px` : undefined);

  // Convert local paths to CDN URLs
  let cdnSrc = currentSrc;
  let shouldUnoptimize = unoptimized;

  if (typeof currentSrc === "string") {
    // Skip if already a full URL or data URL
    if (currentSrc.startsWith("http") || currentSrc.startsWith("data:") || currentSrc.startsWith("blob:")) {
      // 2026-07-03: Polymarket killed hotlinking through their image
      // optimizer (polymarket.com/_next/image now resets the connection),
      // which broke EVERY PM S3 avatar/team-logo site-wide. The raw S3
      // bucket is now publicly readable (200 — the old "403 IP-restricted"
      // note is stale), so serve it DIRECTLY and unoptimized: the browser
      // fetches S3 itself. This restores the images AND costs ZERO origin/
      // Railway egress — the bytes go S3→browser and never transit our
      // server or our Next.js optimizer.
      if (currentSrc.includes("polymarket-upload.s3") || currentSrc.includes("polymarket-upload.amazonaws")) {
        cdnSrc = currentSrc;
        shouldUnoptimize = true;
      } else if (currentSrc.includes(" ")) {
        // Generic: encode raw spaces so Next.js Image Optimizer doesn't
        // reject the URL. encodeURI would double-encode already-encoded
        // chars — do only spaces.
        cdnSrc = currentSrc.replace(/ /g, "%20");
      } else {
        cdnSrc = currentSrc;
      }
      // Check if this external URL should skip optimization
      if (!unoptimized && shouldSkipOptimization(currentSrc)) {
        shouldUnoptimize = true;
      }
    }
    // Skip user uploads (they're already on R2 with full URL)
    else if (currentSrc.startsWith("/uploads/")) {
      // Keep as is - will be served by Next.js (legacy) or already full CDN URL
      cdnSrc = currentSrc;
    }
    // Convert local static paths to CDN
    else if (currentSrc.startsWith("/")) {
      cdnSrc = `${CDN_URL}/${STATIC_PREFIX}${currentSrc}`;
    } else {
      cdnSrc = `${CDN_URL}/${STATIC_PREFIX}/${currentSrc}`;
    }
  }

  return (
    <Image
      src={cdnSrc}
      unoptimized={shouldUnoptimize}
      sizes={effectiveSizes}
      onError={(event) => {
        if (fallbackSrc && currentSrc !== fallbackSrc) {
          setCurrentSrc(fallbackSrc);
          return;
        }
        onError?.(event);
      }}
      {...props}
    />
  );
}

// Memoize to prevent re-renders from parent price updates causing image flicker
const CdnImage = memo(CdnImageInner);
export default CdnImage;
